import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Typography, Alert, Empty, Tag, Space, Input, Button, Pagination } from 'antd';
import { TeamOutlined, SearchOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { fiscalAdminService, programacionDescargasService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import FichaFiscal from './FichaFiscal';
import ResponsiveTable from '../../components/Common/ResponsiveTable';
import ProgramacionDescargasModal from '../../components/Common/ProgramacionDescargasModal';

const { Title, Text } = Typography;

/**
 * Cartera del fiscal-admin: punto de entrada único.
 *
 * Lista los contribuyentes que alcanza, permite localizarlos por RFC o nombre y
 * abre la gestión fiscal sobre la empresa elegida en la misma pantalla. No hay
 * sección de gestión aparte: se entra siempre por el cliente.
 *
 * El alcance se deriva de los planes asignados, así que es dinámico: un cliente
 * que cambia a un plan fuera de la cartera simplemente deja de aparecer.
 */
const Cartera = () => {
  const { isAdmin } = useAuth();

  const [clientes, setClientes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState('');
  const [seleccion, setSeleccion] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(20);
  const [programacionAbierta, setProgramacionAbierta] = useState(false);

  /**
   * Paginación y búsqueda las resuelve el SERVIDOR.
   *
   * La lista ya no llega completa, así que filtrar en el cliente solo
   * alcanzaría la página visible: diría "sin coincidencias" habiéndolas en la
   * página siguiente.
   */
  const fetchCartera = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: pagina, per_page: porPagina };
      if (filtro.trim()) params.q = filtro.trim();

      const data = await fiscalAdminService.getMiCartera(params);
      setClientes(data?.clientes || []);
      setTotal(data?.paginacion?.total ?? data?.total ?? 0);
      setError('');
    } catch (err) {
      setError('Error al cargar la cartera');
      console.error('Error fetching cartera:', err);
    } finally {
      setLoading(false);
    }
  }, [pagina, porPagina, filtro]);

  useEffect(() => {
    fetchCartera();
  }, [fetchCartera]);

  /**
   * Una fila por EMPRESA, no por cliente: el RFC vive en la empresa y un
   * cliente puede tener varias. Aplanarlo evita que la tabla tenga que elegir
   * cuál RFC mostrar.
   */
  const filas = useMemo(() => {
    const salida = [];

    clientes.forEach((cliente) => {
      if (!cliente.empresas || cliente.empresas.length === 0) {
        salida.push({
          key: cliente.id,
          clienteId: cliente.id,
          cliente: cliente.name,
          email: cliente.email,
          plan: cliente.plan_name,
          companyId: null,
          rfc: null,
          razonSocial: null,
        });
        return;
      }

      cliente.empresas.forEach((empresa) => {
        salida.push({
          key: `${cliente.id}:${empresa.id}`,
          clienteId: cliente.id,
          cliente: cliente.name,
          email: cliente.email,
          plan: cliente.plan_name,
          companyId: empresa.id,
          rfc: empresa.tax_id,
          razonSocial: empresa.tax_name,
        });
      });
    });

    return salida;
  }, [clientes]);

  const columns = [
    {
      title: 'Contribuyente',
      dataIndex: 'cliente',
      key: 'cliente',
      render: (nombre, fila) => (
        <Space direction="vertical" size={0}>
          <Text strong>{nombre}</Text>
          {fila.razonSocial && fila.razonSocial !== nombre && (
            <Text type="secondary" style={{ fontSize: 12 }}>{fila.razonSocial}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'RFC',
      dataIndex: 'rfc',
      key: 'rfc',
      render: (rfc) => (rfc ? <Tag color="geekblue">{rfc}</Tag> : <Tag>Sin empresa</Tag>),
    },
    { title: 'Correo', dataIndex: 'email', key: 'email' },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan) => (plan ? <Tag color="blue">{plan}</Tag> : <Tag>Sin plan</Tag>),
    },
    {
      title: '',
      key: 'accion',
      render: (_, fila) =>
        fila.companyId ? (
          <Button size="small" onClick={() => setSeleccion(fila)}>
            Abrir
          </Button>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>Sin empresa registrada</Text>
        ),
    },
  ];

  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  // Gestión de la empresa elegida, sobre la misma pantalla. El regreso lo dibuja
  // la ficha, que lo mantiene fijo al desplazarse.
  if (seleccion) {
    return (
      <FichaFiscal
        companyId={seleccion.companyId}
        onVolver={() => setSeleccion(null)}
        volverTexto="Cartera"
        titulo={seleccion.cliente}
        rfc={seleccion.rfc}
      />
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              <TeamOutlined /> Mi cartera
            </Title>
            <Text type="secondary">
              {isAdmin
                ? 'Como Admin ves todos los contribuyentes, sin restricción de cartera.'
                : 'Contribuyentes de los planes asignados a tu perfil. Si un cliente cambia de plan, deja de aparecer aquí.'}
            </Text>
          </div>

          {/* El alcance del proceso es exactamente lo que hay en esta pantalla:
              las empresas de esta cartera con e.firma cargada. */}
          <Button
            icon={<CloudDownloadOutlined />}
            onClick={() => setProgramacionAbierta(true)}
          >
            Programar descargas
          </Button>
        </div>

        <Input.Search
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar por RFC, nombre o correo"
          defaultValue={filtro}
          onSearch={(valor) => {
            setPagina(1);
            setFiltro(valor);
          }}
          style={{ maxWidth: 420 }}
        />

        <ResponsiveTable
          rowKey="key"
          loading={loading}
          dataSource={filas}
          columns={columns}
          scroll={{ x: true }}
          // La tabla NO pagina: la paginación la hace el servidor y el paginador
          // va aparte, debajo.
          //
          // Dejarla paginar rompía la lista. El total es de CLIENTES y las filas
          // son EMPRESAS, así que una página trae más filas que `per_page` en
          // cuanto alguien tiene dos empresas; antd detecta
          // `dataSource.length > pageSize` y vuelve a rebanar lo que el servidor
          // ya había recortado, escondiendo RFC que no aparecían en NINGUNA
          // página.
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                description={
                  filtro
                    ? `Sin coincidencias para "${filtro}" en tu cartera`
                    : 'Tu cartera está vacía. Un Admin debe asignarte al menos un plan.'
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />

        {/* Cuenta CLIENTES, que es la unidad que pagina el servidor. Las filas
            de arriba son empresas y pueden ser más. */}
        <Pagination
          current={pagina}
          pageSize={porPagina}
          total={total}
          showSizeChanger
          pageSizeOptions={[20, 50, 100, 200]}
          showTotal={(t) => `${t} contribuyente${t === 1 ? '' : 's'}`}
          onChange={(p, tam) => {
            setPagina(p);
            setPorPagina(tam);
          }}
          style={{ textAlign: 'right' }}
        />
      </Space>

      <ProgramacionDescargasModal
        abierto={programacionAbierta}
        onCerrar={() => setProgramacionAbierta(false)}
        cargarAlcance={programacionDescargasService.getAlcanceFiscal}
        procesarTanda={programacionDescargasService.procesarFiscal}
        titulo="Programar descargas de tu cartera"
      />
    </Card>
  );
};

export default Cartera;
